import React, { useState } from 'react';
import { Button, Input, InputNumber, Select, Table, Tabs, message } from 'antd';
import {
  fetchSimplifiedOptionAuditAdmin,
  SimplifiedOptionAuditFilters,
  SimplifiedOptionAuditKind,
} from './adminDataApi';

type Props = {
  simplifiedOptionId?: number;
};

const AUDIT_TABS: Array<{
  key: SimplifiedOptionAuditKind;
  label: string;
}> = [
  { key: 'issuance_quotes', label: 'Frozen issuances' },
  { key: 'order_snapshots', label: 'Player snapshots' },
  { key: 'settlement_orders', label: 'Expiry settlements' },
];

const ORDER_STATUS_OPTIONS = [
  { value: 0, label: 'Created' },
  { value: 1, label: 'Executed' },
  { value: 2, label: 'Canceled' },
];

const SETTLEMENT_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
];

/** Shows bounded read-only audit records without adding any mutation route for derived economics. */
const SimplifiedOptionAuditTables: React.FC<Props> = ({
  simplifiedOptionId,
}) => {
  const [kind, setKind] =
    useState<SimplifiedOptionAuditKind>('issuance_quotes');
  const [sessionId, setSessionId] = useState<number>();
  const [t0ScenarioDate, setT0ScenarioDate] = useState('');
  const [expiry, setExpiry] = useState('');
  const [optionType, setOptionType] = useState<0 | 1>();
  const [status, setStatus] =
    useState<SimplifiedOptionAuditFilters['status']>();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);

  /** Refreshes the selected audit stream with narrow option/session filters. */
  const refresh = async () => {
    setLoading(true);
    try {
      setRows(
        await fetchSimplifiedOptionAuditAdmin(kind, {
          simplified_option_id: simplifiedOptionId,
          session_id: sessionId,
          t0_scenario_date: t0ScenarioDate || undefined,
          expiry: expiry || undefined,
          option_type: optionType,
          status: kind === 'issuance_quotes' ? undefined : status,
          limit: 200,
        }),
      );
    } catch (error) {
      message.error(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  };

  const keys = Array.from(new Set(rows.flatMap(row => Object.keys(row))));

  return (
    <div>
      <Tabs
        activeKey={kind}
        onChange={value => {
          setKind(value as SimplifiedOptionAuditKind);
          setStatus(undefined);
          setRows([]);
        }}
        items={AUDIT_TABS}
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <InputNumber
          min={1}
          precision={0}
          placeholder="Optional session ID"
          value={sessionId}
          onChange={value =>
            setSessionId(value == null ? undefined : Number(value))
          }
        />
        <Input
          type="date"
          aria-label="T0 scenario date"
          value={t0ScenarioDate}
          onChange={event => setT0ScenarioDate(event.target.value)}
        />
        <Input
          type="date"
          aria-label="Expiry date"
          value={expiry}
          onChange={event => setExpiry(event.target.value)}
        />
        <Select<0 | 1>
          allowClear
          placeholder="Option type"
          value={optionType}
          onChange={setOptionType}
          options={[
            { value: 0, label: 'Call' },
            { value: 1, label: 'Put' },
          ]}
          style={{ minWidth: 130 }}
        />
        {kind === 'order_snapshots' && (
          <Select<number>
            allowClear
            placeholder="Status"
            value={typeof status === 'number' ? status : undefined}
            onChange={setStatus}
            options={ORDER_STATUS_OPTIONS}
            style={{ minWidth: 130 }}
          />
        )}
        {kind === 'settlement_orders' && (
          <Select<'PENDING' | 'COMPLETED' | 'FAILED'>
            allowClear
            placeholder="Status"
            value={typeof status === 'string' ? status : undefined}
            onChange={setStatus}
            options={SETTLEMENT_STATUS_OPTIONS}
            style={{ minWidth: 130 }}
          />
        )}
        <Button onClick={refresh} loading={loading}>
          Refresh audit
        </Button>
      </div>
      <Table
        size="small"
        bordered
        rowKey={row => String(row.id)}
        loading={loading}
        dataSource={rows}
        scroll={{ x: 'max-content' }}
        columns={keys.map(key => ({
          title: key.replaceAll('_', ' '),
          dataIndex: key,
          render: value =>
            value == null
              ? '-'
              : typeof value === 'object'
                ? JSON.stringify(value)
                : String(value),
        }))}
      />
    </div>
  );
};

export default SimplifiedOptionAuditTables;
